import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Link } from 'gatsby';
import React from 'react';
import { EarlypayLogo } from '../../atom/logo/EarlypayLogo';
import {
	ArrowRightIcon,
	CaretDownIcon,
	HamburgerMenuIcon,
} from '@radix-ui/react-icons';
import { useNavigation } from '../../../hooks/useNavigation';
import {
	GatsbyImage,
	IGatsbyImageData,
	StaticImage,
} from 'gatsby-plugin-image';
import {
	ContactsMenu,
	LoginsMenu,
	ProductListItem,
	SubmenuLink,
} from './Navigation';
import clsx from 'clsx';

export const NoScriptNav = ({
	theme = 'dark',
}: {
	theme?: 'dark' | 'light';
}) => {
	const { products } = useNavigation();
	const getToKnowUsLinks = [
		{ title: 'About', path: '/about' },
		{ title: 'Investors', path: '/investors' },
	];
	return (
		<nav
			className="h-14 desktop-nav:h-16 fixed desktop-nav:absolute p-0 w-screen top-0 left-0 flex flex-col desktop-nav:flex-row justify-center items-center z-[100] "
			data-theme={theme}
		>
			<div className="relative z-1 w-full flex flex-col desktop-nav:flex-row desktop-nav:items-center h-14 desktop-nav:h-16 max-w-[1200px]">
				<label className="peer/hamburger-menu-btn absolute right-2 top-[6px] w-11 h-11 flex items-center justify-center desktop-nav:hidden cursor-pointer">
					<input type="checkbox" className="hidden peer/checkbox" />
					<VisuallyHidden>Hamburger Menu Button</VisuallyHidden>
					<HamburgerMenuIcon
						className={clsx('peer-checked/checkbox:text-neutral-800', {
							'text-white': theme === 'dark',
							'text-neutral-800': theme === 'light',
						})}
					/>
				</label>
				<Link
					to="/"
					className={
						'hidden top-4 left-6 shrink-0 peer-has-[input:checked]/hamburger-menu-btn:flex desktop-nav:peer-has-[input:checked]/hamburger-menu-btn:hidden items-center absolute'
					}
				>
					<EarlypayLogo
						color={'blue'}
						className="group-peer-has-[input:checked]/hamburger-menu-btn:bg-black"
					/>
				</Link>
				<div
					className={clsx(
						'group flex shrink-0 desktop-nav:mr-10 h-full desktop-nav:h-auto px-6 desktop-nav:px-0 peer-has-[input:checked]/hamburger-menu-btn:bg-white',
						{ 'bg-ep-navy': theme === 'dark' }
					)}
				>
					<Link to="/" className={'shrink-0 flex items-center'}>
						<VisuallyHidden>Earlypay Logo</VisuallyHidden>
						<EarlypayLogo color={theme === 'dark' ? 'white' : 'blue'} />
					</Link>
				</div>
				<div className="no-js__inner-nav hidden peer-has-[input:checked]/hamburger-menu-btn:flex overflow-scroll desktop-nav:overflow-visible fixed desktop-nav:relative top-14 desktop-nav:top-0 bg-white desktop-nav:bg-transparent z-100 w-screen flex-col desktop-nav:flex-row desktop-nav:flex flex-grow">
					<ul className="flex flex-col desktop-nav:flex-row desktop-nav:flex-grow desktop-nav:items-center">
						<DropdownLink
							linkTitle={'What We Do'}
							menuTitle={'Our Products'}
							menuClassName="!desktop-nav:px-4 desktop-nav:min-w-[400px]"
							theme={theme}
						>
							{products.map(
								(product: {
									title: string;
									tagline: string;
									image: { gatsbyImageData: IGatsbyImageData };
									link: string;
									icon: string;
								}) => (
									<ProductListItem
										title={product.title}
										tagline={product.tagline}
										image={product.image}
										link={`/what-we-do${product.link}`}
										icon={product.iconName}
										key={product.link}
									/>
								)
							)}
						</DropdownLink>

						<DropdownLink
							linkTitle={'Company'}
							menuTitle="Get to know us"
							menuClassName="desktop-nav:min-w-[200px]"
							theme={theme}
						>
							{getToKnowUsLinks.map((item: { title: string; path: string }) => (
								<SubmenuLink {...item} key={item.path} />
							))}
						</DropdownLink>

						<DropdownLink
							linkTitle="Resources"
							menuClassName="desktop-nav:min-w-[160px]"
							theme={theme}
						>
							<SubmenuLink title="Blog" path="/blog" />
							<SubmenuLink title="Case Studies" path="/case-studies" />
							<Link
								to="/resources"
								className="mt-4 flex items-center text-fineprint font-medium hover:text-ep-blue group px-6 desktop-nav:px-0"
							>
								<span>See overview</span>
								<ArrowRightIcon
									className="ml-1 text-neutral-400 group-hover:text-ep-blue transition-transform group-hover:translate-x-1"
									width={12}
								/>
							</Link>
						</DropdownLink>

						<li className="mr-2 last:mr-0 relative border-b border-b-solid border-b-neutral-200 desktop-nav:border-none">
							<Link
								to="/partners"
								className={clsx(
									'px-6 desktop-nav:px-3 text-fineprint relative flex items-center h-14 desktop-nav:h-auto',
									{
										'desktop-nav:text-white': theme === 'dark',
										'text-neutral-800': theme === 'light',
									}
								)}
							>
								Partners
							</Link>
						</li>

						<DropdownLink
							linkTitle="Integrations"
							menuClassName="desktop-nav:min-w-[160px]"
							theme={theme}
						>
							<SubmenuLink title="MYOB" path="/myob" />
							<SubmenuLink title="Xemplo" path="/xemplo" />
							<SubmenuLink title="Xero" path="/xero" />
							<Link
								to="/integrations"
								className="mt-4 flex items-center text-fineprint font-medium hover:text-ep-blue group px-6 desktop-nav:px-0"
							>
								<span>See all</span>
								<ArrowRightIcon
									className="ml-1 text-neutral-400 group-hover:text-ep-blue transition-transform group-hover:translate-x-1"
									width={12}
								/>
							</Link>
						</DropdownLink>
					</ul>

					<ul className="flex flex-col desktop-nav:flex-row flex-grow desktop-nav:items-center desktop-nav:justify-end">
						<DropdownLink
							linkTitle="Contact"
							menuClassName="!px-0 !pt-1"
							contentAlign="right"
							theme={theme}
						>
							<ContactsMenu />
						</DropdownLink>

						<DropdownLink
							linkTitle="Login"
							menuClassName="!p-0"
							contentAlign="right"
							theme={theme}
						>
							<LoginsMenu />
						</DropdownLink>

						<li>
							<Link
								to={'/sign-up'}
								className={'navigation__menu__trigger navigation__menu__cta'}
							>
								Sign Up
							</Link>
						</li>
					</ul>
				</div>
			</div>
		</nav>
	);
};

const DropdownLink = ({
	linkTitle,
	menuClassName,
	children,
	menuTitle,
	contentAlign = 'left',
	theme = 'dark',
}: {
	linkTitle: string;
	menuClassName?: string;
	children: string | JSX.Element | (string | JSX.Element)[];
	menuTitle?: string;
	contentAlign?: 'left' | 'right';
	theme?: 'dark' | 'light';
}) => (
	<li className="group desktop-nav:mr-2 last:mr-0 relative border-b border-b-solid border-b-neutral-200 desktop-nav:border-none">
		<label
			className={clsx(
				'cursor-pointer h-14 desktop-nav:h-auto w-full desktop-nav:w-auto flex items-center text-fineprint peer/nav px-6 desktop-nav:px-3 peer/checkbox',
				{
					'desktop-nav:text-white': theme === 'dark',
					'text-neutral-800': theme === 'light',
				}
			)}
		>
			<input type="checkbox" className="hidden peer/hidden-checkbox" />
			{linkTitle}
			<CaretDownIcon className="relative w-5 h-5 desktop-nav:w-[15px] desktop-nav:h-[15px] ml-[2px] top-[1px] peer-checked/hidden-checkbox:rotate-180 desktop-nav:group-hover:rotate-180 transition-transform" />
		</label>
		<div
			className={clsx(
				'hidden desktop-nav:peer-hover/nav:flex desktop-nav:group-hover:flex pt-3 desktop-nav:absolute peer-has-[input:checked]/checkbox:flex',
				{
					'right-0': contentAlign === 'right',
					'left-0': contentAlign === 'left',
				}
			)}
		>
			<ul
				className={clsx(
					'w-full flex-col desktop-nav:pt-5 pb-4 desktop-nav:pb-5 desktop-nav:px-5 bg-white overflow-hidden desktop-nav:rounded-lg',
					menuClassName
				)}
				style={
					{
						// boxShadow:
						// 	'0px 4px 20px 0px rgba(0, 23, 97, 0.1), 0px 1px 2px 0px rgba(0, 33, 118, 0.1)',
					}
				}
			>
				{menuTitle ? (
					<div className={'px-6 desktop-nav:px-0 desktop-nav:mb-3 mb-5'}>
						{menuTitle}
					</div>
				) : null}
				{children}
			</ul>
		</div>
	</li>
);
